import React, { useEffect, useState } from "react"
import { Button, CircularProgress, IconButton, Paper, TextField, Theme, makeStyles } from "@material-ui/core"
import { Smile } from "react-feather"
import { useTranslation } from "react-i18next"
import { useEmojiInsert } from "../hooks/use-emoji-insert"
import EmojiPicker from "./EmojiPicker"

type Props = {
  initialContent: string
  saving?: boolean
  onSubmit: (content: string) => void
  onCancel: () => void
}

const MessageEditor = ({ initialContent, saving, onSubmit, onCancel }: Props) => {
  const classes = useStyles()
  const { t } = useTranslation("translation")
  const { t: chat } = useTranslation("chat")

  const [content, setContent] = useState(initialContent)

  // Its own picker, so emoji land here rather than in the composer's draft below.
  const { inputRef, emojiAnchor, toggleEmojiPicker, closeEmojiPicker, insertEmoji } = useEmojiInsert(
    content,
    setContent
  )

  // On open only: on every focus it would undo clicking into the middle of the text.
  useEffect(() => {
    const input = inputRef.current

    input?.setSelectionRange(input.value.length, input.value.length)
  }, [inputRef])

  const trimmed = content.trim()
  const canSave = Boolean(trimmed) && trimmed !== initialContent.trim() && !saving

  return (
    <Paper
      variant="outlined"
      className={classes.root}
      onKeyDown={(event) => {
        if (event.nativeEvent.isComposing) {
          return // dead key / IME composition in progress, don't submit
        }

        if (event.key === "Escape") {
          if (emojiAnchor) closeEmojiPicker()
          else onCancel()
        }

        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault()

          if (canSave) onSubmit(trimmed)
        }
      }}
    >
      <TextField
        variant="outlined"
        size="small"
        fullWidth
        multiline
        autoFocus
        value={content}
        inputRef={inputRef}
        onChange={(event) => setContent(event.target.value)}
      />

      <div className={classes.actions}>
        <IconButton
          size="small"
          aria-label={chat("emojiButtonLabel")}
          onClick={(event) => toggleEmojiPicker(event.currentTarget)}
        >
          <Smile size={18} />
        </IconButton>

        <EmojiPicker anchorEl={emojiAnchor} onSelect={insertEmoji} onClose={closeEmojiPicker} />

        <div className={classes.spacer} />

        <Button size="small" onClick={onCancel}>
          {t("cancel")}
        </Button>

        <Button size="small" color="primary" variant="contained" disabled={!canSave} onClick={() => onSubmit(trimmed)}>
          {saving ? <CircularProgress size={16} /> : t("save")}
        </Button>
      </div>
    </Paper>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: 420,
    maxWidth: "100%",
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },

  actions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },

  spacer: {
    flex: 1,
  },
}))

export default MessageEditor
